import { Plus } from "lucide-react";
import type { ReactNode } from "react";
import { useRef } from "react";

import { LoadingNote } from "@/components/ui/loading";
import { RefreshButton, useFetchingKeys } from "@/components/ui/refresh-button";
import { Skeleton } from "@/components/ui/skeleton";
import { useDelayed } from "@/hooks/use-delayed";
import { useLoadMore } from "@/hooks/use-load-more";
import { cn } from "@/utils/utils";
import { useTranslation } from "@/store/locale";

/** Enough outlined rows to fill the box without pretending to know the count. */
const SKELETON_ROWS = 5;

/**
 * Every list on the configuration screens, in one shape.
 *
 * There were six of these, each with its own heading row, its own scroll box
 * and its own idea of what to do at the end of the rows — one still had a
 * numbered pager. A screen where each panel behaves differently is one you
 * have to learn six times.
 *
 * The section owns the scrolling and the loading, so a caller passes rows and
 * an action and nothing else. The counter reads "shown of total", because on
 * an infinite list the interesting question is how much of it you have seen.
 */
export function ListSection({
  title,
  shown,
  total,
  refreshKeys,
  refresh = true,
  action,
  hasMore,
  loadingMore,
  pending,
  onLoadMore,
  empty,
  className,
  listClassName,
  children,
}: {
  title: ReactNode;
  /** Rows on screen now. */
  shown: number;
  /** Rows there are altogether; from the first page, so it does not flicker. */
  total: number | undefined;
  refreshKeys: readonly (readonly unknown[])[];
  /**
   * Whether the heading offers a refresh. The keys are still needed either
   * way — they are what tells the section a read is in flight — so this turns
   * off the button alone, for a list that is written and read in one place and
   * has nothing a reload would show.
   */
  refresh?: boolean;
  /** The "Add …" button, or anything else this list does. */
  action?: ReactNode;
  hasMore: boolean;
  loadingMore: boolean;
  pending: boolean;
  onLoadMore: () => void;
  empty: ReactNode;
  className?: string;
  /** For a list that wants its scroll box bordered rather than bare. */
  listClassName?: string;
  children: ReactNode;
}) {
  const t = useTranslation();
  const list = useRef<HTMLUListElement>(null);
  // A refresh keeps the rows — they are still true until the answer says
  // otherwise — so there is no skeleton to show and the spinning icon in the
  // corner is the only sign anything is happening. Say it in words beside the
  // count instead. Delayed, so a refetch that lands in 80ms shows nothing.
  const busy = useFetchingKeys(refreshKeys);
  const refreshing = useDelayed(busy);
  // `busy` and not just `loadingMore`: a refresh refetches every page already
  // loaded, so scrolling to the end while one is in flight would ask for a page
  // that answer is about to bring anyway.
  useLoadMore(list, hasMore, loadingMore || busy, onLoadMore);

  return (
    <section className={className}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <span className="flex min-w-0 flex-wrap items-baseline gap-2">
          <h3 className="text-heading text-callout font-medium break-words">{title}</h3>
          {total === undefined ? null : (
            <span className="text-fg-muted text-footnote tabular-nums">
              {t("common.shownOfTotal")
                .replace("{shown}", shown.toLocaleString())
                .replace("{total}", total.toLocaleString())}
            </span>
          )}
          {refreshing ? <LoadingNote /> : null}
        </span>
        {/* A fieldset, so one flag disables every control in the heading — the
            refresh that is already running and whatever the caller put beside
            it — without the caller having to thread the state through. It
            reaches form controls only, which is why the section rail and the
            tab strip stay navigable: leaving a list that is loading is fine,
            acting on one that is about to change is not. */}
        {refresh || action ? (
          <fieldset disabled={busy} className="ml-auto flex shrink-0 items-center gap-1">
            {refresh ? <RefreshButton queryKeys={refreshKeys} iconOnly /> : null}
            {action}
          </fieldset>
        ) : null}
      </div>

      <ul
        ref={list}
        aria-busy={busy || undefined}
        className={cn(
          "divide-line max-h-[26rem] divide-y overflow-y-auto",
          "hairline-t",
          // Re-reading the list dims the rows rather than replacing them, the
          // way the table on a ResourcePage does: they are still true until the
          // answer says otherwise, and the note above says what is happening.
          "transition-opacity duration-[var(--duration-base)]",
          busy && shown > 0 && "opacity-55",
          listClassName,
        )}
      >
        {children}
        {/* Rows in outline while they load: the list keeps its height, so the
            heading and the Add beside it do not jump when the real ones land.
            A word saying "Loading" collapses the box to one line and then
            shoves everything down. */}
        {pending && shown === 0
          ? Array.from({ length: SKELETON_ROWS }, (_, index) => (
              <li key={index} className="flex items-center justify-between gap-4 py-2.5">
                <Skeleton className={index % 2 === 0 ? "w-1/2" : "w-1/3"} />
                <Skeleton className="w-14" />
              </li>
            ))
          : null}
        {!pending && shown === 0 ? (
          <li className="text-fg-muted text-callout py-10 text-center">{empty}</li>
        ) : null}
        {/* The scroll listener fetches on its own; this is for a pointer that
            never scrolls and for a keyboard, which cannot reach a scroll end. */}
        {hasMore ? (
          <li className="py-3 text-center">
            <button
              type="button"
              onClick={onLoadMore}
              disabled={loadingMore || busy}
              className="text-accent text-callout font-medium hover:underline disabled:opacity-60"
            >
              {loadingMore ? t("common.loading") : t("common.loadMore")}
            </button>
          </li>
        ) : null}
      </ul>
    </section>
  );
}

/** The action beside a list's heading. Accent, quiet until pointed at. */
export function AddButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-accent hover:bg-accent-soft text-callout inline-flex items-center gap-1 rounded-lg px-2 py-1 font-medium transition-colors disabled:pointer-events-none disabled:opacity-50"
    >
      <Plus className="size-4" aria-hidden /> {children}
    </button>
  );
}
